/* @clinic-interest-report-v1 (7 Oct 2026)
 *
 * Which coming-soon clinic do patients want most? Reads the anonymous tap
 * counter the app has been filling since the clinic chooser shipped
 * (POST /clinics/:id/interest -> clinic_interest).
 *
 * ANONYMOUS BY DESIGN. The counter holds a number per clinic and nothing about
 * who tapped -- "patient X wants the PrEP clinic" would be Article 9 data. This
 * page must never be extended to show people. It is also why clinic taps are
 * NOT sent to Firebase / Google Analytics.
 *
 * Read the numbers as TAPS, not people: one patient tapping five times counts
 * five. Taps are recorded only while a clinic is not live.
 */

import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { clinicInterestAPI } from '../services/api';

const S = {
    page: { padding: '24px', maxWidth: '1000px', margin: '0 auto' },
    head: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '16px', marginBottom: '8px' },
    sub: { color: '#6b7280', fontSize: '14px', marginBottom: '20px', maxWidth: '720px' },
    btn: { padding: '9px 16px', background: '#111827', color: '#fff', border: 0, borderRadius: '6px', fontSize: '14px', cursor: 'pointer' },
    h2: { fontSize: '16px', margin: '28px 0 10px' },
    tableWrap: { border: '1px solid #e5e7eb', borderRadius: '8px', overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th: { textAlign: 'left', padding: '10px 12px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap', fontWeight: 600 },
    thNum: { textAlign: 'right', padding: '10px 12px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap', fontWeight: 600 },
    td: { padding: '10px 12px', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' },
    tdNum: { padding: '10px 12px', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap', textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
    barCell: { padding: '10px 12px', borderBottom: '1px solid #f3f4f6', width: '35%' },
    barTrack: { height: '8px', background: '#f3f4f6', borderRadius: '4px' },
    bar: { height: '8px', background: '#C0185A', borderRadius: '4px' },
    muted: { color: '#6b7280' },
    empty: { padding: '40px', textAlign: 'center', color: '#6b7280' },
};

const dt = (d) => (d ? new Date(d).toLocaleDateString('el-GR', { dateStyle: 'short' }) : '—');

// Taps per day since the first tap. A clinic added last week is not "less
// wanted" than one that has been on the home screen for two months.
function perDay(row) {
    if (!row.firstTapAt || !row.tapCount) return null;
    const days = Math.max(1, (Date.now() - new Date(row.firstTapAt).getTime()) / 86400000);
    return row.tapCount / days;
}

export default function ClinicInterestPage() {
    const [rows, setRows] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        clinicInterestAPI.list()
            .then((r) => setRows(r.data?.data || []))
            .catch((err) => toast.error(err.response?.data?.error || 'Δεν φόρτωσε η αναφορά.'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const soon = (rows || []).filter((r) => !r.clinicIsActive);
    const live = (rows || []).filter((r) => r.clinicIsActive && r.tapCount > 0);
    const max = Math.max(1, ...soon.map((r) => r.tapCount || 0));
    const total = soon.reduce((s, r) => s + (r.tapCount || 0), 0);

    return (
        <div style={S.page}>
            <div style={S.head}>
                <h1 style={{ margin: 0, fontSize: '22px' }}>Ενδιαφέρον για κλινικές</h1>
                <button style={S.btn} type="button" onClick={load} disabled={loading}>
                    {loading ? 'Φόρτωση…' : 'Ανανέωση'}
                </button>
            </div>
            <p style={S.sub}>
                Πόσες φορές πατήθηκε στην εφαρμογή το πλακίδιο κάθε κλινικής που δεν έχει ανοίξει ακόμη.
                Ανώνυμο: μετράει πατήματα, όχι άτομα (ο ίδιος ασθενής που πατά δύο φορές μετράει δύο) και
                δεν καταγράφει ποιος πάτησε.
            </p>

            {rows === null ? (
                <div style={S.empty}>{loading ? 'Φόρτωση…' : '—'}</div>
            ) : soon.length === 0 ? (
                <div style={S.empty}>Δεν υπάρχουν κλινικές «σύντομα κοντά σας» αυτή τη στιγμή.</div>
            ) : (
                <>
                    <h2 style={S.h2}>Σύντομα κοντά σας · {total} πατήματα συνολικά</h2>
                    <div style={S.tableWrap}>
                        <table style={S.table}>
                            <thead>
                                <tr>
                                    <th style={S.th}>Κλινική</th>
                                    <th style={S.thNum}>Πατήματα</th>
                                    <th style={S.th}></th>
                                    <th style={S.thNum}>Ανά ημέρα</th>
                                    <th style={S.th}>Πρώτο</th>
                                    <th style={S.th}>Τελευταίο</th>
                                </tr>
                            </thead>
                            <tbody>
                                {soon.map((r) => {
                                    const pd = perDay(r);
                                    return (
                                        <tr key={r.serviceTypeId}>
                                            <td style={S.td}>{r.nameEl || r.nameEn}</td>
                                            <td style={S.tdNum}><strong>{r.tapCount}</strong></td>
                                            <td style={S.barCell}>
                                                <div style={S.barTrack}>
                                                    <div style={{ ...S.bar, width: `${(100 * (r.tapCount || 0)) / max}%` }} />
                                                </div>
                                            </td>
                                            <td style={S.tdNum}>{pd == null ? <span style={S.muted}>—</span> : pd.toFixed(1).replace('.', ',')}</td>
                                            <td style={S.td}>{dt(r.firstTapAt)}</td>
                                            <td style={S.td}>{dt(r.lastTapAt)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {live.length > 0 && (
                <>
                    <h2 style={S.h2}>Ήδη ανοιχτές (πατήματα πριν ανοίξουν)</h2>
                    <div style={S.tableWrap}>
                        <table style={S.table}>
                            <thead>
                                <tr>
                                    <th style={S.th}>Κλινική</th>
                                    <th style={S.thNum}>Πατήματα</th>
                                    <th style={S.th}>Τελευταίο πριν το άνοιγμα</th>
                                </tr>
                            </thead>
                            <tbody>
                                {live.map((r) => (
                                    <tr key={r.serviceTypeId}>
                                        <td style={S.td}>{r.nameEl || r.nameEn}</td>
                                        <td style={S.tdNum}>{r.tapCount}</td>
                                        <td style={S.td}>{dt(r.lastTapAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}
